/**
 * analyContract — `muzly-backend-api` vocal 모듈의 진단(analy) 계약을 앱 쪽에 옮긴 것.
 *
 * 정본은 서버의 `vocal-module/SPEC.md`(PR #3, 2026-08-27)이고 여기는 사본이다. 사본을
 * 두는 이유는 하나다: 필드 이름을 틀리면 서버가 `400 INVALID`로 돌려주는데, 그 실패는
 * 실제로 진단을 마친 사용자에게서만 일어난다. 타입으로 잡으면 컴파일에서 끝난다.
 *
 * **값 구조는 서버가 바꾸지 않는다**(SPEC D34) — 서버의 `AnalyFieldPart`는 잘못된 테이크에
 * 실린 필드를 거절하는 검증 규칙이지 변환기가 아니다. 그래서 이 파일의 모양이 곧 서버에
 * 저장되는 모양이다.
 *
 * 읽기 필드(`AnalyView` 쪽)의 정본은 SPEC §2.2(`model.ts`)·§2.3(`views.ts`)이고, 합성
 * 경로는 §3.3(목록)·§3.4(상세)다.
 */

import type { CaptureSource } from '../capture';

/** 하위 진단 4종. `''`(메인)·`'#'`(더미)는 제출 판별자에서 빠진다 (SPEC §2.1). */
export type AnalyStereo = 'sustain' | 'low' | 'high' | 'song';

/** 메인 진단의 상태. `''`=생성 직후, `pending`=하위 진행 중, `done`=4종 완료. */
export type AnalyState = '' | 'pending' | 'done';

/**
 * 회차 점수 축 (SPEC §2.1 `AnalyAxisKey`). 앱의 `STORED_METRIC_KEYS`와 **값이 같다** —
 * 매핑 테이블을 두지 않는 이유이자, 한쪽이 늘면 다른 쪽도 늘어야 하는 이유다.
 */
export type AnalyAxisKey =
  | 'pitchAccuracy'
  | 'pitchStability'
  | 'sustain'
  | 'vibrato';

/** 축 점수 한 줄. 서버도 맵이 아니라 배열로 받는다. */
export interface AnalyScore {
  key: AnalyAxisKey;
  val: number;
}

/** 음역 구간 하나 (SPEC §2.2 `PitchRegister`). */
export interface AnalyPitchRegister {
  id?: string;
  lowMidi?: number;
  highMidi?: number;
  score?: number;
}

export interface AnalyVibrato {
  rateHz?: number;
  depthCents?: number;
  regularityPct?: number;
}

export interface AnalyBreathSegment {
  id?: 'start' | 'middle' | 'late';
  score?: number;
}

export interface AnalySongTiming {
  meanOffsetMs?: number;
  onTimeRatio?: number;
  matchedNotes?: number;
}

/**
 * 노래 채점의 음역대별 통계 (SPEC §2.2 `SongPartScore`).
 *
 * 회차 전체(`AnalySongScore`)와 **필드가 다르다** — 구간별로는 정확도·정타비율이 아니라
 * 편차와 경향을 담는다. 앱의 `SongSegmentScore`가 그대로 이 모양이다.
 */
export interface AnalySongPartScore {
  sungNotes?: number;
  totalNotes?: number;
  /** 평균 편차(cent). 부른 음이 모자라면 없다. */
  centDev?: number;
  /** `'flat'` · `'sharp'` · `'onPitch'`. 서버는 문자열로만 받는다. */
  tendency?: string;
}

export interface AnalySongScore {
  accuracy?: number;
  hitRatio?: number;
  sungNotes?: number;
  totalNotes?: number;
  low?: AnalySongPartScore;
  high?: AnalySongPartScore;
  timing?: AnalySongTiming;
}

/**
 * 회차 산출물 — 테이크가 아니라 **진단 한 회차**의 결과다.
 *
 * 서버에서 이 셋은 `AnalySubmitCommon`에 얹혀 있어 4종 **어느 제출에도** 실린다. `song`에
 * 묶여 있지 않다는 게 중요하다: 재시도 큐 때문에 제출 순서가 뒤집히면 `song`이 먼저
 * 도착할 수 있고, 그때 집계(D20)가 산출물 없는 제출에서 발화한다.
 */
export interface AnalySessionResult {
  scores?: AnalyScore[];
  pitchRegisters?: AnalyPitchRegister[];
  fach?: string;
}

interface AnalySubmitCommon extends AnalySessionResult {
  /** 이번 테이크 점수 0~100. 앱이 계산한다 (서버는 담기만 한다 — D1·D32). */
  score?: number;
  mediaId?: string;
  algoVersion?: string;
  /**
   * 녹음 조건 — 네이티브 녹음이 있을 때만 실린다(plan Spec). backend PR #4
   * (`feat/take-capture`) 기준으로 서버가 값을 그대로 하위의 `capture$`에 저장한다 —
   * 제출마다 덮어쓰고, 이 키가 없으면 `{}`다(`submit-take.ts` STEP.4, D41).
   *
   * **완료(`done`)된 진단은 이 키가 갈림길이다**(D42): 비어 있지 않은 `capture`가 실린 제출은
   * (그 하위가 이미 있으면) 하위의 `capture$`만 바꾸고 200이다 — 지표·`mediaId`·집계는
   * 반영되지 않는다. 없거나 비어 있으면 409다. `analySession`은 409를
   * "닫힘"으로 보고 남은 큐를 버린다. 그래서 마감 큐의 제출은 네이티브 녹음이 있는 한 모두
   * `capture`를 싣는다(`useRecordMachine.analy.spec`이 고정한다).
   */
  capture?: AnalyCapture;
}

/**
 * 녹음 조건 (서버 `AnalyCapture`) — 진단 테이크가 **어떤 환경에서** 녹음됐는가.
 * 필드 이름·의미의 정본은 서버 `src/modules/vocal/model.ts`의 `AnalyCapture`(backend PR #4
 * `feat/take-capture` 기준)라 여기는 사본이다. 서버는 값을 검사하거나 바꾸지 않고 그대로
 * 저장하며, 그 타입 키 밖의 항목도 담는다(인덱스 시그니처). `toAnalyCapture`가
 * `NativeRecording`에서 명시로 옮긴다 — 통째로 흘려보내지 않는다(`toSongPartScore`와
 * 같은 규칙). 서버 키 목록과의 대조는 `toAnalyCapture.spec`이 고정한다.
 */
export interface AnalyCapture {
  platform?: 'ios' | 'android';
  captureSource?: 'engine_input' | 'unprocessed' | 'voice_performance' | 'mic';
  sessionMode?: 'measurement' | 'default';
  inputRoute?: 'builtin' | 'wired' | 'bluetooth' | 'usb' | 'other';
  inputRouteRaw?: string;
  inputDataSource?: string;
  backingTrack?: boolean;
  /**
   * 테이크가 시스템 에코 제거(iOS VPIO)를 실제로 거쳤나 · 그 판정의 근거 출력 포트.
   * `voiceProcessing`은 서버 `AnalyCapture`의 타입 키가 아니다 — 인덱스 시그니처로
   * 그대로 저장된다(`outputRouteRaw`는 타입 키다).
   */
  voiceProcessing?: boolean;
  /** iOS VPIO 의 AGC readback. 인덱스 시그니처로 저장된다. */
  agcEnabled?: boolean;
  outputRouteRaw?: string;
  /**
   * 하드웨어가 준 IO 버퍼·입출력 지연(ms). 서버 `AnalyCapture`의 타입 키가 아니라
   * 인덱스 시그니처로 그대로 저장된다.
   */
  ioBufferMs?: number;
  inputLatencyMs?: number;
  outputLatencyMs?: number;
  /** Android `AudioSource.UNPROCESSED` 지원 여부. 인덱스 시그니처로 저장된다. */
  unprocessedSupported?: boolean;
  /**
   * 테이크를 잰 분석 조건 — 진단 캡처 config(`diagnosisCaptureConfig`)의 값. 서버 재분석이 같은
   * 조건으로 다시 재는 입력이다. 인덱스 시그니처로 저장된다.
   */
  silenceThresholdDb?: number;
  confidenceThreshold?: number;
  windowSize?: number;
  hopSize?: number;
  sampleRate?: number;
  bitDepth?: number;
  durationSec?: number;
  gapCount?: number;
  gapOffsets?: number[];
  clipSampleCount?: number;
  peakDbfs?: number;
  degradedFrameCount?: number;
  /** 게이트가 잡은 방 소음 바닥(dBFS). 못 잡은 것도 잰 사실이라 `null`로 싣는다. */
  noiseFloorDbfs?: number | null;
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
}

/**
 * 테이크별 지표를 body와 **따로** 선언한다 (SPEC이 `SustainMetrics`·`LowMetrics`·
 * `HighMetrics`·`SongMetrics`로 나눠 둔 것과 같은 모양).
 *
 * 나누는 이유가 타입 안전이다: 매퍼가 지표를 담을 때 이 타입을 명시해야 필드명 오타가
 * 컴파일에서 죽는다. 리터럴을 익명으로 두면 TypeScript가 그 리터럴에서 타입을 추론해
 * 초과 필드 검사가 사라진다 — 스프레드로 body에 합쳐질 때도 검사가 되살아나지 않는다.
 */
export interface AnalySustainMetrics {
  sustainSec?: number;
  hnrDb?: number;
  resonanceRatio?: number;
  resonanceLevelDb?: number;
  resonanceCenterHz?: number;
  brightnessHz?: number;
  bandwidthHz?: number;
  vibrato?: AnalyVibrato;
  breath?: AnalyBreathSegment[];
}

/**
 * 지속음의 소리 안정성 곡선(SPEC §2.2 `AnalyGraph`) — 화면이 그린 상대 높낮이 창을
 * 0(창 하단)~100(창 상단)으로 정규화한 시계열. cents도 MIDI 절대값도 아니다
 * (`apps/web/CONTEXT.md` "Stability Curve").
 */
export interface AnalyGraph {
  /** y값, 0~100 정수. 최대 1,000점(SPEC §4). */
  values: number[];
  /** 표본 간격(ms), 양수. */
  interval: number;
  /** 총 길이(ms). 양수(서버 `proxy.ts:138` `<= 0` → 400). */
  duration: number;
}

export interface SustainSubmitBody
  extends AnalySubmitCommon,
    AnalySustainMetrics {
  stereo: 'sustain';
  graph$?: AnalyGraph;
}

/**
 * `lowNote`는 **노트 이름 문자열**(`"F#2"`)로 보낸다. 서버가 받아 MIDI 정수로 파싱해
 * 저장한다 — SPEC D37이 그 결정이고 §4 검증표가 재확인한다. D37은 앱의 `range.lowNote`가
 * 이미 문자열이라는 실측을 근거로 세워졌으므로(옥타브 기준 `C4=60`까지 실측 대조됨) 앱은
 * 변환 없이 그대로 보낸다. SPEC의 `LowMetrics.lowNote: number` 선언은 D37 이전의 낡은
 * 표기다 — 전송 형태가 아니라 저장 형태를 적은 것이다.
 */
export interface AnalyLowMetrics {
  lowNote?: string;
  lowHz?: number;
}

export interface LowSubmitBody extends AnalySubmitCommon, AnalyLowMetrics {
  stereo: 'low';
}

export interface AnalyHighMetrics {
  highNote?: string;
  highHz?: number;
}

export interface HighSubmitBody extends AnalySubmitCommon, AnalyHighMetrics {
  stereo: 'high';
}

export interface AnalySongMetrics {
  songScore?: AnalySongScore;
}

export interface SongSubmitBody extends AnalySubmitCommon, AnalySongMetrics {
  stereo: 'song';
}

export type AnalySubmitBody =
  | SustainSubmitBody
  | LowSubmitBody
  | HighSubmitBody
  | SongSubmitBody;

/**
 * 앱 측정·채점 알고리즘 버전(SPEC §2.2 `AnalyModel.algoVersion`). 서버는 문자열을
 * 그대로 담을 뿐 형식을 강제하지 않는다 — 이 `<source>-v<N>` 형식은 앱 쪽 결정이다.
 *
 * **이 숫자를 올리는 규칙**: `record/toDiagnosisRecord.ts`의 `overallScore()`·축 산식,
 * `toAnalyGraph`의 스케일 산식, 또는 같은 녹음에서 다른 값을 내는 측정(캡처 게이트·DSP)이 바뀌면 +1 한다. 서버 이력 복원(`fromAnalyView`)이
 * 과거 회차와 지금 산출값을 구분해야 할 때 이 값을 본다.
 *
 * 3 (2026-10-15): 진단 무음 게이트 -45→-60, 저음 끝 지지 320ms, MPT 쉼 허용 250ms, 비브라토
 * 15c·4Hz — 같은 녹음이 다른 sustainSec·lowHz·vibrato 를 낸다.
 */
export const ANALY_ALGO_VERSION = 3;

/** 캡처 경로별 알고리즘 버전 문자열 (`native-v3`·`mic-v3`·`mock-v3`). */
export const algoVersionOf = (source: CaptureSource): string =>
  `${source}-v${ANALY_ALGO_VERSION}`;

/**
 * 회차 축 점수 맵 — 키·개수 가변(SPEC §2.2 `VocalTerrain`, D33). 앱 `AnalyAxisKey`가 키다.
 *
 * 값이 `| undefined`인 이유: 이 레포는 `noUncheckedIndexedAccess`를 켜지 않았으므로
 * `[axis: string]: number`만 두면 없는 축을 읽어도 타입이 `number`로 나온다(런타임 `undefined`,
 * 컴파일은 통과). 소비부가 항상 `?.`·존재 확인을 거치게 강제한다.
 */
export interface AnalyTerrain {
  [axis: string]: number | undefined;
}

/**
 * 음역 합성 — 저음·고음 두 테이크에서 서버가 만든다(SPEC §2.2 `VocalRange`, 앱은 못 만든다).
 * `lowNote`·`highNote`는 **MIDI 정수**로 저장된다 — 쓰기 쪽 `AnalyLowMetrics.lowNote`(노트
 * 이름 문자열)와 이름은 같아도 모양이 다르니 그대로 옮기지 않는다.
 */
export interface AnalyRange {
  lowNote?: number;
  highNote?: number;
  lowHz?: number;
  highHz?: number;
  semitones?: number;
}

/**
 * 쓰기용 `AnalyLowMetrics`·`AnalyHighMetrics`의 `lowNote`/`highNote`는 노트 이름 문자열이다.
 * 읽기(`metrics$`)에서는 그대로 재사용하지 않는다 — 쓰기는 노트 문자열, 읽기는 MIDI 정수
 * (서버 D37, `transformer.ts` `asMidi`로 저장 시 변환).
 */
export type AnalyLowMetricsView = Omit<AnalyLowMetrics, 'lowNote'> & {
  lowNote?: number;
};

export type AnalyHighMetricsView = Omit<AnalyHighMetrics, 'highNote'> & {
  highNote?: number;
};

/** 하위 제출 4종 지표의 합집합(SPEC §2.2 `AnalyMetrics`) — `metrics$`에 stereo별로 채워진다. */
export type AnalyMetricsView = AnalySustainMetrics &
  AnalyLowMetricsView &
  AnalyHighMetricsView &
  AnalySongMetrics;

/**
 * 하위 진단의 원음 미디어(SPEC §2.3 `media$`). 앱은 `url`만 쓴다. 소유자·관리자 응답에만 실린다 —
 * 비소유자 상세 응답에는 `mediaId` 와 함께 빠진다(muzly-io/muzly-backend-api#5).
 */
export interface AnalyMedia {
  id?: string;
  stereo?: string;
  url?: string;
}

/**
 * `GET /analy/{id}/detail`·`GET /analy/0/list?detail=1` 응답 중 앱이 실제로 쓰는 것만
 * (SPEC §2.3 `AnalyView`). 서버 뷰는 이보다 넓다(`$parent`·`progress$` 등 — 이 플랜은
 * 안 쓴다).
 */
export interface AnalyView {
  id?: string;
  state?: AnalyState;
  /**
   * 하위 제출의 판별자 — 메인은 `''`(`$LUT.AnalyStereo`의 키, `types.ts:105`; 값은
   * `'initial'`). 제출 전용 `AnalyStereo`(4-union)와 달리 읽기 뷰는 이 빈 문자열도 온다.
   *
   * `'#'`(비색인 더미, `types.ts:103`)는 유니언에서 뺀다 — `submitToModel`의
   * `parts.includes` 판별(`transformer.ts:250-252`)이 제출 시 즉시 거절해 모델에 저장되지
   * 않으므로 읽기 뷰에도 나타나지 않는다.
   */
  stereo?: AnalyStereo | '';
  /** 메인: 종합점수 · 하위: 테이크 점수(0~100). */
  score?: number;
  /**
   * 이 앱이 보낸 하위엔 실린다(그 전 회차엔 없다 — 복원은 없으면 native로 폴백) — `$childs[]`
   * 각 항목도 마찬가지다: `submitToModel`(`muzly-backend-api/src/modules/vocal/
   * transformer.ts:269`)이 제출 body의 `algoVersion`을 있을 때만 그대로 옮기므로, 슬라이스
   * 01 전에 이 앱이 보낸 회차의 하위 모델엔 이 필드가 없다. 메인엔 **done 전이 시에만**,
   * 마지막 제출본에서 온다(`muzly-backend-api/src/lib/vocal/sync-children.ts:57-77`의
   * `isDone` 분기가 `aggregate.ts:114-115`의 `$last?.algoVersion`을 메인에 쓴다 — 그전
   * 메인에는 없다).
   */
  algoVersion?: string;
  /** 4종 완료 시각(ms epoch) — 메인에만 실린다. `fromAnalyView`의 로컬 id(`d<completedAt>`) 재료. */
  completedAt?: number;
  /**
   * 지운 시각 — 서버가 소프트 삭제한 행에 찍힌다. 목록이 이 행을 숨기는지는 dev 에서 확인하지
   * 못했다(미확인) — 숨기지 않아도 복원·삭제가 이 행을 건드리지 않게 읽는 쪽이 거른다.
   */
  deletedAt?: number | string;
  /** 성부 — 회차 산출물, 메인에만. */
  fach?: string;
  terrain$?: AnalyTerrain;
  range$?: AnalyRange;
  registers$?: AnalyPitchRegister[];
  /** 하위 제출의 지표 — `stereo`로 어느 필드가 채워졌는지 가른다. */
  metrics$?: AnalyMetricsView;
  /**
   * 하위가 올린 원음의 미디어 id — 업로드가 안 된 테이크엔 없다(ADR-0007). 메인 뷰에는 없다.
   */
  mediaId?: string;
  /**
   * `mediaId`를 풀어 낸 재생용 미디어(서버 `transformer.ts`). `url`은 바뀔 수 있는 주소라
   * 저장하지 않고 열 때마다 읽는다. 메인 뷰에는 없다.
   */
  media$?: AnalyMedia;
  /**
   * `detail=1` 목록·상세 응답이 합성해 싣는 하위 제출 4종(readonly, 서버가 채운다).
   * `graph$`는 목록에서는 빠진다(SPEC §3.3) — 상세에서만 실린다.
   */
  readonly $childs?: AnalyView[];
}
